class ArticleGallery extends HTMLElement {
  connectedCallback() {
    const contentEl = this.querySelector('.swiper.swiper-content');
    const galleryEl = this.querySelector('.swiper.swiper-gallery');
    if (!contentEl || !galleryEl) return;

    this.pagesOnlyEls = this.querySelectorAll('[data-gallery-pages-only]');
    this.gallerySwiper = null;
    this.contentSwiper = null;
    this.tabbyInstance = null;
    this._targetSlideIndex = null;
    this._seekGeneration = 0;
    this._dialogWasActive = false;
    this._onDocumentClick = this._handleDocumentClick.bind(this);
    this._onTabKeydown = this._handleTabKeydown.bind(this);
    this._onDialogTriggerCapture = this._handleDialogTriggerCapture.bind(this);
    this._onGridItemClick = this._handleGridItemClick.bind(this);
    this._dialogTriggerCaptureEvents = [];

    this._bindDialogTriggerCapture();
    this.addEventListener('click', this._onGridItemClick);

    const dialog = document.getElementById('gallery-dialog');
    if (dialog) {
      this._dialogObserver = new MutationObserver(() => {
        const isActive = dialog.classList.contains('active');

        if (isActive && !this._dialogWasActive) {
          this._dialogWasActive = true;
          this._resolveOpenIntent();
          return;
        }

        if (!isActive && this._dialogWasActive) {
          this._dialogWasActive = false;
          this._seekGeneration += 1;
          this._targetSlideIndex = null;
        }
      });
      this._dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['class'] });
    }

    whenReady('Swiper', () => {
      this.contentSwiper = new Swiper(contentEl, {
        slidesPerView: 1,
        spaceBetween: 8,
        loop: true,
        a11y: true,
        effect: 'fade',
        fadeEffect: {
          crossFade: true,
        },
      });

      this.gallerySwiper = new Swiper(galleryEl, {
        slidesPerView: 1,
        spaceBetween: 8,
        navigation: {
          nextEl: this.querySelector('.swiper-next'),
          prevEl: this.querySelector('.swiper-prev'),
        },
        loop: true,
        a11y: true,
        effect: 'fade',
        fadeEffect: {
          crossFade: true,
        },
        mousewheel: {
          forceToAxis: true,
        },
        controller: {
          control: this.contentSwiper,
        },
      });

      this.contentSwiper.controller.control = this.gallerySwiper;

      this.gallerySwiper.on('slideChange', () => {
        if (this._targetSlideIndex !== null) return;
        this._syncThumbActiveState(this.gallerySwiper.realIndex);
      });

      if (dialog?.classList.contains('active')) {
        this._resolveOpenIntent();
      }
    });

    whenReady('Tabby', (Tabby) => {
      const tabList = this.querySelector('[data-tabby]');
      if (!tabList || !tabList.id) return;

      document.documentElement.addEventListener('click', this._onDocumentClick, true);
      tabList.addEventListener('keydown', this._onTabKeydown, true);

      this.tabbyInstance = new Tabby(`#${tabList.id}`);

      if (!dialog?.classList.contains('active')) {
        this.setViewMode(tabList.querySelector('[role="tab"][aria-selected="true"]'));
      }
    });

    this.addEventListener('tabby', (event) => {
      if (!this.contains(event.detail.tab)) return;

      this.setViewMode(event.detail.tab);
      if (event.detail.tab.hash === '#article-gallery-pages') {
        this.updateSwipers();
      }
    });
  }

  disconnectedCallback() {
    this._unbindDialogTriggerCapture();
    this.removeEventListener('click', this._onGridItemClick);
    document.documentElement.removeEventListener('click', this._onDocumentClick, true);
    this._dialogObserver?.disconnect();

    const tabList = this.querySelector('[data-tabby]');
    if (tabList) {
      tabList.removeEventListener('keydown', this._onTabKeydown, true);
    }
  }

  _bindDialogTriggerCapture() {
    const events = 'PointerEvent' in window
      ? ['pointerdown']
      : ['touchstart', 'mousedown'];

    events.forEach((eventName) => {
      document.addEventListener(eventName, this._onDialogTriggerCapture, true);
      this._dialogTriggerCaptureEvents.push(eventName);
    });
  }

  _unbindDialogTriggerCapture() {
    this._dialogTriggerCaptureEvents.forEach((eventName) => {
      document.removeEventListener(eventName, this._onDialogTriggerCapture, true);
    });
    this._dialogTriggerCaptureEvents = [];
  }

  setOpenIntent({ view = 'pages', slideIndex = null } = {}) {
    const dialog = document.getElementById('gallery-dialog');
    const hasSlide = slideIndex !== null && !Number.isNaN(slideIndex);

    this._targetSlideIndex = hasSlide ? slideIndex : null;

    if (!dialog) return;

    dialog.dataset.galleryOpenView = view;

    if (hasSlide) {
      dialog.dataset.galleryOpenSlide = String(slideIndex);
      return;
    }

    delete dialog.dataset.galleryOpenSlide;
  }

  getOpenView() {
    const dialog = document.getElementById('gallery-dialog');
    return dialog?.dataset.galleryOpenView || 'pages';
  }

  clearOpenView() {
    const dialog = document.getElementById('gallery-dialog');
    if (dialog) {
      delete dialog.dataset.galleryOpenView;
      delete dialog.dataset.galleryOpenSlide;
    }
  }

  _resolveOpenIntent() {
    const dialog = document.getElementById('gallery-dialog');
    if (!dialog?.classList.contains('active')) return;

    const view = this.getOpenView();
    const slideIndex = this._targetSlideIndex;

    this.clearOpenView();

    const run = () => {
      if (!dialog.classList.contains('active')) return;
      if (!this.tabbyInstance || !this.gallerySwiper) {
        window.setTimeout(run, 50);
        return;
      }

      this.activateView(view, { animate: false });

      if (view === 'pages' && slideIndex !== null) {
        this._seekToSlide(slideIndex);
        return;
      }

      if (view === 'pages') {
        this.updateSwipers();
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  }

  openToSlide(index, { animate = false } = {}) {
    if (Number.isNaN(index)) return;

    const dialog = document.getElementById('gallery-dialog');
    this.setOpenIntent({ view: 'pages', slideIndex: index });

    if (!dialog?.classList.contains('active')) return;

    if (this.dataset.view === 'grid') {
      this._openGridItem(index);
      return;
    }

    const seek = () => {
      this._seekToSlideAfterPagesVisible(index);
    };

    if (animate) {
      this.transitionToggle(() => {
        this.activateView('pages', { animate: false });
      }, seek);
      return;
    }

    this.activateView('pages', { animate: false });
    seek();
  }

  _openGridItem(index) {
    if (Number.isNaN(index)) return;

    const seekGeneration = ++this._seekGeneration;
    this.setOpenIntent({ view: 'pages', slideIndex: index });
    this.setAttribute('data-preparing-slide', '');

    this.transitionToggle(() => {
      this.activateView('pages', { animate: false });
    }, () => {
      this._seekToSlideAfterPagesVisible(index, 0, () => {
        if (seekGeneration !== this._seekGeneration) return;

        this.removeAttribute('data-preparing-slide');
        this._targetSlideIndex = null;
        this._syncThumbActiveState(this.gallerySwiper?.realIndex ?? index);
      }, seekGeneration);
    });
  }

  activateView(view, { animate = true } = {}) {
    if (!this.tabbyInstance) {
      const dialog = document.getElementById('gallery-dialog');
      if (dialog) {
        dialog.dataset.galleryOpenView = view;
      }
      return;
    }

    const tabList = this.querySelector('[data-tabby]');
    if (!tabList) return;

    const tab = tabList.querySelector(`[role="tab"][href="#article-gallery-${view}"]`);
    if (!tab) return;

    if (tab.getAttribute('aria-selected') === 'true') {
      this.setViewMode(tab);
      return;
    }

    const switchTab = () => {
      this.tabbyInstance.toggle(tab);
      this.setViewMode(tab);
    };

    if (animate) {
      this.transitionToggle(switchTab);
      return;
    }

    switchTab();
  }

  _handleDialogTriggerCapture(event) {
    const trigger = event.target.closest('[data-a11y-dialog-show="gallery-dialog"]');
    if (!trigger) return;

    const dialog = document.getElementById('gallery-dialog');
    if (!dialog) return;

    const slide = trigger.getAttribute('data-gallery-slide');
    const hasSlide = slide !== null && slide !== '';
    const view = trigger.getAttribute('data-gallery-view') || 'pages';
    const slideIndex = hasSlide ? parseInt(slide, 10) : null;

    if (dialog.classList.contains('active')) {
      if (hasSlide) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openToSlide(slideIndex);
      }
      return;
    }

    this.setOpenIntent({
      view,
      slideIndex: hasSlide ? slideIndex : null,
    });
  }

  _handleGridItemClick(event) {
    const gridItem = event.target.closest('[data-gallery-grid-slide]');
    if (!gridItem || !this.contains(gridItem)) return;
    if (this.dataset.view !== 'grid') return;

    const index = parseInt(gridItem.getAttribute('data-gallery-grid-slide'), 10);
    if (Number.isNaN(index)) return;

    event.preventDefault();
    this._openGridItem(index);
  }

  transitionToggle(callback, onComplete) {
    if (document.startViewTransition) {
      const transition = document.startViewTransition(callback);

      if (onComplete) {
        transition.finished.then(onComplete).catch(onComplete);
      }

      return;
    }

    callback();

    if (onComplete) {
      requestAnimationFrame(() => {
        requestAnimationFrame(onComplete);
      });
    }
  }

  _handleDocumentClick(event) {
    const tabList = this.querySelector('[data-tabby]');
    if (!tabList || !this.tabbyInstance) return;

    const tab = event.target.closest(`#${tabList.id} [role="tab"]`);
    if (!tab || !this.contains(tab)) return;
    if (tab.getAttribute('aria-selected') === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.transitionToggle(() => {
      this.tabbyInstance.toggle(tab);
    });
  }

  _handleTabKeydown(event) {
    const tabList = this.querySelector('[data-tabby]');
    if (!tabList || !this.tabbyInstance) return;

    const tab = document.activeElement;
    if (!tab.matches(`#${tabList.id} [role="tab"]`)) return;

    const navKeys = ['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight', 'Home', 'End'];
    if (!navKeys.includes(event.key)) return;

    const tabs = Array.from(tabList.querySelectorAll('[role="tab"]'));
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex === -1) return;

    const lastIndex = tabs.length - 1;
    let nextIndex;

    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = currentIndex < 1 ? lastIndex : currentIndex - 1;
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else {
      nextIndex = lastIndex;
    }

    const nextTab = tabs[nextIndex];
    if (!nextTab || nextTab === tab) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.transitionToggle(() => {
      this.tabbyInstance.toggle(nextTab);
    });
  }

  setViewMode(tab) {
    const isPagesView = tab && tab.hash === '#article-gallery-pages';

    this.dataset.view = isPagesView ? 'pages' : 'grid';

    this.pagesOnlyEls.forEach((el) => {
      el.hidden = !isPagesView;
    });
  }

  updateSwipers() {
    requestAnimationFrame(() => {
      this.gallerySwiper?.update();
      this.contentSwiper?.update();
    });
  }

  _seekToSlideAfterPagesVisible(index, attempt = 0, onComplete = null, generation = null) {
    const pagesPanel = this.querySelector('#article-gallery-pages');
    const isVisible = pagesPanel && !pagesPanel.hasAttribute('hidden');

    if ((!isVisible || !this.gallerySwiper || !this.contentSwiper) && attempt < 20) {
      window.setTimeout(
        () => this._seekToSlideAfterPagesVisible(index, attempt + 1, onComplete, generation),
        50
      );
      return;
    }

    this._seekToSlide(index, 0, generation, onComplete);
  }

  _seekToSlide(index, attempt = 0, generation = null, onComplete = null) {
    if (Number.isNaN(index)) {
      onComplete?.();
      return;
    }

    const seekGeneration = generation ?? ++this._seekGeneration;
    const maxAttempts = 10;

    const apply = () => {
      if (seekGeneration !== this._seekGeneration) return;
      if (!this.gallerySwiper || !this.contentSwiper) {
        if (attempt < maxAttempts) {
          window.setTimeout(() => this._seekToSlide(index, attempt + 1, seekGeneration, onComplete), 50);
        } else {
          onComplete?.();
        }
        return;
      }

      this.gallerySwiper.update();
      this.contentSwiper.update();
      this.gallerySwiper.slideToLoop(index, 0);

      window.setTimeout(() => {
        if (seekGeneration !== this._seekGeneration) return;

        if (this.gallerySwiper.realIndex === index || attempt >= maxAttempts) {
          this._targetSlideIndex = null;
          this._syncThumbActiveState(this.gallerySwiper.realIndex);
          onComplete?.();
          return;
        }

        this._seekToSlide(index, attempt + 1, seekGeneration, onComplete);
      }, 75);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(apply);
    });
  }

  _syncThumbActiveState(index) {
    if (Number.isNaN(index)) return;

    document.querySelector('article-gallery-thumbs')?.setActiveThumb?.(index);
  }
}

customElements.define('article-gallery', ArticleGallery);
